import { productIsAffiliate } from '@gik/checkout/utils/productUtils';
import type { CartItem } from '@gik/core/models/gik/Order';
import type { Product } from '@gik/core/models/gik/Product';
import { CheckoutType } from '@gik/core/models/gik/Product';
import { useEnvStore } from '@gik/core/store/EnvStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ProductStructuredData } from '@gik/seo/components/StructuredData';
import { getProductDetailsFormSchema } from '@gik/shop/components/products/ProductDetails/formSchema';
import type { OLProductType } from '@gik/shop/components/products/ProductSection/ProductSection';
import { defaultToggleValue, getDefaultFaceplate } from '@gik/shop/hooks/useOpenLoop';
import { Decoder } from '@gik/ui/Decoder';
import type { FormRef } from '@gik/ui/Form';
import { Form, FormField } from '@gik/ui/Form';
import { ParagraphBone } from '@gik/ui/SkeletonLoader';
import { SvgIcon } from '@gik/ui/SvgIcon';
import { Tooltip } from '@gik/ui/Tooltip';
import InformationCircleIcon from '@heroicons/react/solid/InformationCircleIcon';
import React from 'react';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode';
import { useGYSelectMinMaxValue } from '@gik/ui/ProductPrice';

export interface IProductDetailsValues {
  [x: `item-${number}`]: CartItem;
}

export type ProductDetailsProps = {
  product: Product;
  toggleValue?: OLProductType;
  hasNameOnCardField?: boolean;
  onChange?(item: CartItem): void;
};

export const ProductDetailsComp = React.forwardRef<FormRef, ProductDetailsProps>(function ProductDetailsComp(
  { product, toggleValue = defaultToggleValue, hasNameOnCardField, onChange }: ProductDetailsProps,
  ref
) {
  const index = 0;

  const bem = useBemCN('product-details');
  const [selectedCartItem, setSelectedCartItem] = React.useState<CartItem>();
  const [selectMinValue, selectMaxValue] = useGYSelectMinMaxValue();

  const formSchema = React.useMemo(
    () =>
      getProductDetailsFormSchema(
        product,
        toggleValue,
        hasNameOnCardField,
        undefined,
        undefined,
        selectMinValue,
        selectMaxValue
      ),
    [hasNameOnCardField, product, selectMaxValue, selectMinValue, toggleValue]
  );

  const formRef = React.useRef<FormRef>();
  React.useImperativeHandle(ref, () => formRef.current);

  const handleChange = React.useCallback(
    (values: IProductDetailsValues) => {
      if (product) {
        const cartItem = {
          ...values[`item-${index}`],
          productType: values[`productType-${index}`],
          customMessage: values[`customMessage-${index}`],
          uploadPhoto: values[`uploadPhoto-${index}`],
          faceplate: values[`faceplate-${index}`],
          anonymous: values[`anonymous-${index}`],
        };

        // TODO bundle everything in the cart item
        onChange({
          ...cartItem,
          productSlug: product.slug,
        });

        setSelectedCartItem(cartItem);
      }
    },
    [onChange, product]
  );

  function renderSource() {
    return (
      <>
        {!(
          product.checkoutType === CheckoutType.TangoCard ||
          product.checkoutType === CheckoutType.Perfectgift ||
          product.checkoutType === CheckoutType.GiftyaPlatform
        ) &&
          productIsAffiliate(product) &&
          product.sources &&
          product.sources.length && (
            <section {...bem('vendor', [{ external: product.checkoutType === CheckoutType.ExternalCheckout }])}>
              on {product.sources[0].name}
            </section>
          )}
        {(product.checkoutType === CheckoutType.TangoCard ||
          product.checkoutType === CheckoutType.Perfectgift ||
          product.checkoutType === CheckoutType.GiftyaPlatform) && (
          <section {...bem('tc-disclaimer')}>on Give InKind</section>
        )}
      </>
    );
  }

  const anonymousEnabled = useEnvStore(state => state.USE_ANONYMOUS_DONATIONS) === 'true';

  const form = React.useMemo(
    () => (
      <Form
        ref={formRef}
        vertical
        onChange={handleChange}
        id="product-details"
        restoreAfterUpdate
        schema={formSchema}
        initialValues={{
          [`item-${index}`]: selectedCartItem,
          [`productType-${index}`]: product.isToggleable ? toggleValue : undefined,
          [`faceplate-${index}`]: product.isToggleable ? getDefaultFaceplate(product) : undefined,
        }}
        render={form => {
          return (
            <>
              {product.isToggleable && (
                <div>
                  <strong className="productType-title">Select Gift Card Type:</strong>
                  <FormField name={`productType-${index}`} />

                  <div className="productType-description">
                    {form.values[`productType-${index}`] === 'physical' && (
                      <span>Sent by mail inside a custom greeting card. For in-store and online use.</span>
                    )}
                    {form.values[`productType-${index}`] === 'digital' && (
                      <span>Sent via email (without greeting card). For online and phone purchases only.</span>
                    )}
                  </div>
                </div>
              )}

              <FormField
                name={`item-${index}`}
                onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    // prevent the default action of the enter key of the numeric input which is to submit the form
                    event.preventDefault();

                    const nextEl = document.querySelector('[name="anonymous-0"]') as HTMLInputElement;
                    if (nextEl) nextEl.focus();
                  }
                }}
              />

              {anonymousEnabled && selectedCartItem?.checkoutType !== CheckoutType.GikPremium && (
                <div {...bem('anonymous')}>
                  <div>
                    <FormField name={`anonymous-${index}`} />
                  </div>
                  <div>
                    <Tooltip allowOnMobile text={"Your name won't be shown to the Page Organizer or the Recipient"}>
                      <div className="tw-text-neutral-600">
                        <SvgIcon Icon={InformationCircleIcon} />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
              {/* <FormField name={`customMessage-${index}`} /> */}
            </>
          );
        }}
      />
    ),
    [anonymousEnabled, bem, formSchema, handleChange, product, selectedCartItem, toggleValue]
  );

  if (!product) return <ProductDetailsSkeleton />;

  return (
    <ProductStructuredData product={product}>
      <div {...bem(null, [{ [`type-${product.type}`]: product }])}>
        <section {...bem('title', [{ external: product.checkoutType === CheckoutType.ExternalCheckout }])}>
          <h1>
            <Decoder text={product.name} />
            {product.type !== 'fixed' && renderSource()}
          </h1>
          <Debug data={product.checkoutType} />
        </section>
        <section {...bem('price', [{ external: product.checkoutType === CheckoutType.ExternalCheckout }])}>
          {form}
        </section>
        {product.type === 'fixed' && renderSource()}
      </div>
    </ProductStructuredData>
  );
});

export const ProductDetails = withComponentErrorBoundary(ProductDetailsComp);

export function ProductDetailsSkeleton() {
  const bem = useBemCN('product-details-skeleton');
  return (
    <div {...bem()}>
      <ParagraphBone words={8} minWidth={60} maxWidth={90} {...bem('title')} />
      <ParagraphBone words={2} minWidth={40} {...bem('price')} />
    </div>
  );
}
